import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useToast } from "../../context/toast-context";
import { useTranslation } from "react-i18next";
import { UserService } from "../../services/user-service";
import { EmployeeUser } from "../../types";

interface DeleteEmployeeProps {
  userId: number;
  updateEmployees: (updatedEmployees: any) => void;
  branchId?: number;
  size?: "small" | "default";
}

export function DeleteEmployee({
  userId,
  updateEmployees,
  size = "default",
  branchId,
}: DeleteEmployeeProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingBranches, setCheckingBranches] = useState(false);
  const [belongsToOtherBranches, setBelongsToOtherBranches] = useState(false);
  const { t } = useTranslation();
  const { showToast } = useToast();
  const userService = useMemo(() => new UserService(), []);

  const loadIfLinkedToOtherBranches = async () => {
    if (!branchId) {
      setBelongsToOtherBranches(false);
      return;
    }

    setCheckingBranches(true);
    const pageSize = 100;
    const collected: EmployeeUser[] = [];
    let offset = 0;
    let totalCount = Number.MAX_SAFE_INTEGER;

    try {
      while (offset < totalCount) {
        const page = await new Promise<{ users: EmployeeUser[]; totalCount: number }>(
          (resolve, reject) => {
            userService.getEmployees(
              { limit: pageSize, offset },
              resolve,
              reject
            );
          }
        );
        collected.push(...(page.users ?? []));
        totalCount = page.totalCount ?? collected.length;
        offset += pageSize;
        if (!page.users?.length) break;
      }

      setBelongsToOtherBranches(
        collected.some(
          (employee) => employee.userId === userId && employee.branchId !== branchId
        )
      );
    } catch (errorMessage) {
      showToast(String(errorMessage), "error");
      setBelongsToOtherBranches(false);
    } finally {
      setCheckingBranches(false);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    loadIfLinkedToOtherBranches();
  };

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    setBelongsToOtherBranches(false);
  };

  const handleDelete = () => {
    if (checkingBranches) return;
    setLoading(true);
    userService.deleteEmployees(
      userId,
      async () => {
        // Refresco sin filtrar por la sucursal desvinculada: si se filtra,
        // el padre puede reemplazar el listado global y “borrar” al empleado
        // de las demás sucursales hasta un F5.
        const getParams =
          branchId != null
            ? { limit: 100, offset: 0, branchid: branchId }
            : { limit: 100, offset: 0 };

        // Teams ignora el array y hace reset/refetch con su filtro actual.
        // YourTeam / otros reemplazan con este resultado (contexto de una sucursal).
        await userService.getEmployees(
          getParams,
          (data) => {
            // Señal de remoción + lista actualizada para padres que hacen replace
            updateEmployees(
              Object.assign([...data.users], {
                removedUserId: userId,
                removedBranchId: branchId,
              })
            );
            setLoading(false);
            showToast(
              belongsToOtherBranches
                ? t("employee.delete.unlink-success")
                : t("employee.delete.success"),
              "success"
            );
            setOpen(false);
          },
          (error) => {
            showToast(error, "error");
            setLoading(false);
            setOpen(false);
          }
        );
      },
      (error) => {
        showToast(error, "error");
        setLoading(false);
        setOpen(false);
      },
      branchId
    );
  };

  return (
    <>
      <Button
        title={t("employee.delete.label")}
        onClick={handleClickOpen}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: size === "default" ? "2.5rem" : "1.7rem",
          height: size === "default" ? "2.5rem" : "1.7rem",
          padding: size === "small" ? "0.25rem" : "inherit",
        }}
      >
        <svg
          fill="#EF0BB8"
          width="16"
          height="16"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m19.587 16.001 6.096 6.096a1.015 1.015 0 0 1 0 1.435l-2.151 2.151a1.015 1.015 0 0 1-1.435 0L16 19.587l-6.097 6.096a1.014 1.014 0 0 1-1.434 0l-2.152-2.151a1.015 1.015 0 0 1 0-1.435l6.097-6.096-6.097-6.097a1.015 1.015 0 0 1 0-1.435L8.47 6.318a1.014 1.014 0 0 1 1.434 0L16 12.415l6.097-6.097a1.015 1.015 0 0 1 1.435 0l2.151 2.152a1.015 1.015 0 0 1 0 1.435l-6.096 6.096z" />
        </svg>
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby={t("employee.delete.label")}
        aria-describedby={t("employee.delete.label")}
      >
        <DialogTitle
          sx={{
            padding: "20px 24px 8px",
            fontWeight: 400,
            fontSize: "16px",
            borderRadius: "30px",
            "&.MuiDialogTitle-root": {
              fontFamily: "Montserrat",
            },
          }}
          id="delete-employee"
        >
          {belongsToOtherBranches
            ? t("employee.delete.unlink-title")
            : t("employee.delete.title")}
        </DialogTitle>
        {belongsToOtherBranches && (
          <DialogContent sx={{ paddingTop: 0 }}>
            <DialogContentText
              sx={{
                fontFamily: "Montserrat",
                fontSize: "0.875rem",
                color: "#4b4b4b",
              }}
            >
              {t("employee.delete.unlink-message")}
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            sx={{
              fontFamily: "Montserrat",
              color: "#000",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
            }}
            onClick={handleClose}
            color="inherit"
            disabled={loading}
          >
            {t("employee.delete.cancel")}
          </Button>
          <Button
            sx={{
              fontFamily: "Montserrat",
              backgroundColor: "#E60032",
              color: "#fff",
              fontSize: "0.875rem",
              textTransform: "capitalize",
              fontWeight: 300,
              width: belongsToOtherBranches ? "120px" : "90px",
              "&.Mui-disabled": {
                opacity: "40%",
                color: "#fff",
              },
            }}
            onClick={handleDelete}
            disabled={loading || checkingBranches}
          >
            {checkingBranches ? (
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            ) : !loading ? (
              belongsToOtherBranches
                ? t("employee.delete.unlink-submit")
                : t("employee.delete.submit")
            ) : (
              <CircularProgress
                sx={{
                  color: "#fff",
                }}
                size="1.5rem"
                className="animate-spin "
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
